import { Link } from '@tanstack/react-router';
import { splitMentions } from '@dove/shared';
import { cn } from '@/lib/utils';

/**
 * Somebody's writing, with the names in it tappable (docs/5 § D260809-0021).
 *
 * ONE RENDERER FOR EVERY BODY IN THE PRODUCT — an open letter, a comment, a
 * direct letter — for the same reason `ToProfile` is one link: the alternative
 * is three surfaces that agree today and disagree the first time one of them is
 * touched. What varies between them is the type, the measure and the wrapper,
 * and all three stay with the caller; what does not vary is what an `@` means.
 *
 * IT DOES NOT PARSE ANYTHING ITSELF. `splitMentions` is the shared parser the
 * server notifies from, so a name drawn as a link here is exactly a name that
 * reached somebody's notifications. A component with its own regex is how those
 * two silently come apart, and the failure is invisible from either side: text
 * that links but never notified, or a notification for a word nobody could tap.
 *
 * A LINK IS DRAWN WITHOUT ASKING WHETHER ANYBODY HOLDS THE NAME, and that is
 * deliberate rather than lazy. Resolving would mean either a request per body
 * on every screen, or a list of resolved handles on the wire beside every
 * letter — and it would still be answering as of the moment the letter is READ.
 * docs/5 § D260805-0242 already settled what a handle is: it is released when
 * its owner changes it, X's behaviour, taken knowingly, and a link to an old one
 * 404s. That decision made a mention TEXT rather than a pointer to an account.
 * So the letter keeps saying what it said, the tap goes to whoever holds the
 * name now, and a name nobody holds lands on the same "no such person" screen
 * every other dead handle in the product reaches.
 *
 * `dir="auto"` STAYS WITH THE CALLER, not here. A body's direction belongs to
 * the body (`PostCard` argues it), and this is a fragment inside one.
 */
export function Written({
  body,
  className,
}: {
  body: string;
  className?: string;
}) {
  return (
    <>
      {splitMentions(body).map((span, i) =>
        span.handle ? (
          <Link
            key={i}
            to="/u/$handle"
            params={{ handle: span.handle }}
            /* `dir="ltr"` because a handle is `[a-z0-9_]` by rule: inside an
               Arabic paragraph a bidi-neutral run would otherwise reorder around
               its own punctuation and the `@` would swap ends. `unicode-bidi`
               isolates it so doing that costs the sentence around it nothing. */
            dir="ltr"
            className={cn(
              /* `text-link` names a link by role; it currently aliases the
                 theme's readable primary blue. No underline: a body with four
                 underlined runs in it stops looking like writing. */
              /* `inline`, never `inline-block`: a run that cannot break is a
                 20-character word that pushes a narrow column wider. */
              'inline text-link [unicode-bidi:isolate] hover:underline',
              className,
            )}
          >
            {span.text}
          </Link>
        ) : (
          /* A fragment rather than a `<span>`: the body is `whitespace-pre-wrap`
             and an element per run would give the line breaker something to
             break on that the text does not have. */
          <span key={i}>{span.text}</span>
        ),
      )}
    </>
  );
}
